<template>
  <div class="merge-users-dialog" v-if="show">
    <div class="modal fade show" style="display: block;">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">合并用户账号</h5>
            <button type="button" class="btn-close" @click="handleClose"></button>
          </div>
          
          <div class="modal-body">
            <div class="alert alert-info" role="alert">
              <i class="bi bi-info-circle me-2"></i>
              合并后，源用户的所有数据将迁移到目标用户，源用户账号将被删除。
            </div>
            
            <form @submit.prevent="handleSubmit">
              <!-- 源用户选择 -->
              <div class="mb-3">
                <label class="form-label">源用户（将被合并的用户）</label>
                <select 
                  class="form-select" 
                  v-model="sourceUserId"
                  :class="{ 'is-invalid': errors.sourceUserId }"
                  required
                >
                  <option value="">请选择源用户</option>
                  <option 
                    v-for="user in availableSourceUsers" 
                    :key="user.id" 
                    :value="user.id"
                  >
                    {{ user.username }} - {{ user.firstName }} {{ user.lastName }}
                    {{ user.phone ? `(${user.phone})` : '' }}
                  </option>
                </select>
                <div class="invalid-feedback" v-if="errors.sourceUserId">
                  {{ errors.sourceUserId }}
                </div>
              </div>
              
              <!-- 目标用户选择 -->
              <div class="mb-3">
                <label class="form-label">目标用户（合并后保留的用户）</label>
                <select 
                  class="form-select" 
                  v-model="targetUserId"
                  :class="{ 'is-invalid': errors.targetUserId }"
                  required
                >
                  <option value="">请选择目标用户</option>
                  <option 
                    v-for="user in availableTargetUsers" 
                    :key="user.id" 
                    :value="user.id"
                  >
                    {{ user.username }} - {{ user.firstName }} {{ user.lastName }}
                    {{ user.phone ? `(${user.phone})` : '' }}
                  </option>
                </select>
                <div class="invalid-feedback" v-if="errors.targetUserId">
                  {{ errors.targetUserId }}
                </div>
              </div>
              
              <!-- 确认信息 -->
              <div class="alert alert-warning mt-3" v-if="sourceUserId && targetUserId">
                <h6 class="alert-heading">请确认合并信息：</h6>
                <p class="mb-0">
                  将用户 <strong>{{ getSelectedUserInfo(sourceUserId) }}</strong><br>
                  合并到 <strong>{{ getSelectedUserInfo(targetUserId) }}</strong>
                </p>
              </div>
            </form>
          </div>
          
          <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="handleClose"
              :disabled="loading"
            >
              取消
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              @click="handleSubmit"
              :disabled="!isValid || loading"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
              {{ loading ? '合并中...' : '确认合并' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { usersApi } from '../../api/users'

interface User {
  id: number
  username: string
  firstName: string
  lastName: string
  phone?: string
}

interface Props {
  show: boolean
  users: User[]
}

interface Emits {
  (e: 'close'): void
  (e: 'success', mergedUser: User): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// 状态
const sourceUserId = ref<number | ''>('')
const targetUserId = ref<number | ''>('')
const loading = ref(false)
const errors = ref({
  sourceUserId: '',
  targetUserId: ''
})

// 计算属性
const availableSourceUsers = computed(() => {
  return props.users.filter(user => user.id !== targetUserId.value)
})

const availableTargetUsers = computed(() => {
  return props.users.filter(user => user.id !== sourceUserId.value)
})

const isValid = computed(() => {
  return sourceUserId.value && targetUserId.value && sourceUserId.value !== targetUserId.value
})

// 方法
const getSelectedUserInfo = (userId: number) => {
  const user = props.users.find(u => u.id === userId)
  if (!user) return ''
  return `${user.username} - ${user.firstName} ${user.lastName}${user.phone ? ` (${user.phone})` : ''}`
}

const handleClose = () => {
  emit('close')
}

const handleSubmit = async () => {
  if (!isValid.value || loading.value) return
  
  // 重置错误
  errors.value = {
    sourceUserId: '',
    targetUserId: ''
  }
  
  try {
    loading.value = true
    
    const result = await usersApi.mergeUsers({
      sourceUserId: sourceUserId.value as number,
      targetUserId: targetUserId.value as number
    })
    
    emit('success', result)
    handleClose()
  } catch (error: any) {
    if (error.response?.data?.message) {
      if (error.response.data.message.includes('源用户')) {
        errors.value.sourceUserId = error.response.data.message
      } else if (error.response.data.message.includes('目标用户')) {
        errors.value.targetUserId = error.response.data.message
      } else {
        // 显示通用错误
        alert(error.response.data.message)
      }
    } else {
      alert('合并用户失败，请重试')
    }
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.merge-users-dialog {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1050;
}

.modal-dialog {
  max-width: 600px;
}

.alert {
  font-size: 0.9rem;
}

.alert strong {
  color: #084298;
}

.alert-warning strong {
  color: #664d03;
}
</style>